import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { addItem } from '../../actions/CartActions';
import { fetchRegistryByCollectionItemId } from '../../actions/RegistryActions';
import { showModal } from '../../actions/ModalActions';
import DateRange from '../product/DateRange';
import ProductPrice from '../product/ProductPrice';
import ProductImageGallery from '../product/ProductImageGallery';
import CompactCurrencyFilter from '../product/PricingUtils';
import ProductInfoTabs from '../product/ProductInfoTabs';
import ExternalPurchaseFlow from './ExternalPurchaseFlow';
import featureFlags from '../../util/featureFlags';
import { customEventNames, trackEvent } from '../../util/trackingHelper';

const GROUP_GIFT_COPY = 'Contribute any amount you choose towards this gift. If the gift is not completed by the wedding date, the couple will receive all the funds.';

class RegistryItemDp extends Component {
  constructor(props) {
    super(props);

    this.selectTab = this.selectTab.bind(this);
    this.togglePriceDetails = this.togglePriceDetails.bind(this);
    this.addToCart = this.addToCart.bind(this);
    this.buyGroupGift = this.buyGroupGift.bind(this);
    this.updateQuantity = this.updateQuantity.bind(this);
    this.updateContribution = this.updateContribution.bind(this);
    this.onAmountChange = this.onAmountChange.bind(this);

    this.state = {
      currentAmountKey: '',
      selectedTab: 0,
      showPriceDetails: false,
      externalSubmitted: false,
      quantity: 1,
      contribution: '',
      registry: {}
    };
  }

  componentDidMount() {
    const { collectionItem, getRegistryByCollectionItemId } = this.props;
    if ((collectionItem.type === 'EXTERNAL' && !collectionItem.cash_fund) || collectionItem.personal_note) {
      getRegistryByCollectionItemId(collectionItem.object_id).then((registry) => {
        this.setState({
          registry: registry.data
        });
      });
    }
  }

  onAmountChange(e) {
    this.setState({ currentAmountKey: e.target.value });
  }

  selectTab(index) {
    this.setState({
      selectedTab: index
    });
  }

  togglePriceDetails() {
    this.setState({
      showPriceDetails: !this.state.showPriceDetails
    });
  }

  updateQuantity(event) {
    const quantity = parseInt(event.target.value, 10);
    this.setState({
      quantity
    });
  }

  updateContribution(event) {
    const contribution = parseFloat(event.target.value);
    this.setState({
      contribution
    });
  }

  /**
   * Creates payload object for custom event from product and supplemental data
   * @param {Object} product - registry collection item
   * @param {Number} quantity - amount of a product
   * @param {String} cartId - cart id to which product is being added to.
   * @returns {Object} - complete payload for custom event tracking
   */
  buildCustomEventData(product, quantity, cartId) {
    const { registryId, isDefaultGiftCard } = this.props;
    let imageUrl;
    let productLookId;
    let productId;
    let skuId;
    let { price } = product;
    if (isDefaultGiftCard) {
      skuId = this.state.currentAmountKey;
      imageUrl = product.product_look_views[0].image_links.A_300;
      productId = product.id;
      productLookId = product.product_look_views[0].id;
      price = product.product_look_views[0].sku_previews.filter(n => n.id === skuId)[0].price_cents / 100;
    } else {
      skuId = product.sku_object_id;
      imageUrl = product.images[0].medium;
      productId = product.object_id;
      productLookId = product.product_look_id;
    }
    const { name } = product;
    const brand = product.brand && product.brand.name;
    return {
      cart_id: cartId,
      registry_id: registryId,
      product_id: productId,
      sku: skuId,
      name,
      brand,
      price,
      quantity,
      image_url: imageUrl,
      variant: productLookId,
      location: 'WEDDING_PDP',
      section: 'MAIN_CTA'
    };
  }

  addToCart() {
    const { isDefaultGiftCard, registryId, onAddToCart, collectionItem, onCart } = this.props;
    const isGroupGift = !isDefaultGiftCard && collectionItem.contributions.group_gift;
    const { quantity } = this.state;
    let addToCartRequest;
    if (isGroupGift) {
      addToCartRequest = {
        skuId: collectionItem.sku_object_id,
        collectionItemId: collectionItem.object_id,
        quantity,
        contribution: this.state.contribution || 0,
        itemOrigin: 'WEDDINGS'
      };
    } else if (isDefaultGiftCard) {
      addToCartRequest = {
        skuId: this.state.currentAmountKey,
        quantity,
        defaultGiftCardRegistryId: registryId,
        itemOrigin: 'WEDDINGS',
      };
    } else {
      addToCartRequest = {
        skuId: collectionItem.sku_object_id,
        collectionItemId: collectionItem.object_id,
        quantity,
        itemOrigin: 'WEDDINGS'
      };
    }
    if (featureFlags.get('bugFixToGroupContrib')) {
      onAddToCart(addToCartRequest).then((response) => {
        if (featureFlags.get('allowProductAddedEvent')) {
          const { cartId } = response.data;
          const eventData = this.buildCustomEventData(collectionItem, quantity, cartId);
          trackEvent(customEventNames.PRODUCT_ADDED, eventData);
        }
        onCart();
      }).catch((error) => {
        if (error.response.errorData) {
          if (error.response.errorData.isGroupGift) {
            this.setState({
              contribution: error.response.errorData.maxContribution
            });
          }
          if (error.response.errorData.maxContribution > 0) {
            this.setState({ contribution: error.response.errorData.maxContribution });
          } else if (error.response.errorData.availableQuantity > 0) {
            this.setState({ contribution: error.response.errorData.availableQuantity });
          }
        }
      });
    } else {
      onAddToCart(addToCartRequest).then(onCart).catch((error) => {
        if (error.response.errorData) {
          if (error.response.errorData.isGroupGift) {
            this.setState({
              contribution: error.response.errorData.maxContribution
            });
          }
          if (error.response.errorData.maxContribution > 0) {
            this.setState({ quantity: error.response.errorData.maxContribution });
          } else if (error.response.errorData.availableQuantity > 0) {
            this.setState({ quantity: error.response.errorData.availableQuantity });
          }
        }
      });
    }
  }

  buyGroupGift() {
    const { onAddToCart, collectionItem, onCart } = this.props;

    const addToCartRequest = {
      skuId: collectionItem.sku_object_id,
      collectionItemId: collectionItem.object_id,
      quantity: 1,
      buyGroupGift: true
    };

    onAddToCart(addToCartRequest).then(onCart);
  }

  renderDefaultGiftCardOptions(collectionItem) {
    const skuViews = collectionItem.product_look_views && collectionItem.product_look_views[0].sku_previews;
    // in this scenario there's 1:1 mapping of an item and sku attribute key
    const options = skuViews
      .sort((a, b) => a.price_cents - b.price_cents)
      .map((option) => {
        const mapped = { key: option.id, label: option.attribute_values[0].value_label };
        return mapped;
      });
    const { currentAmountKey } = this.state;
    return (
      <div className="product-input form-group">
        <label htmlFor="collection-item-price">Amount:</label>
        <div className="input-wrapper">
          <select className="form-control" name="collection-item-price" onChange={this.onAmountChange} value={currentAmountKey}>
            <option value="" disabled>Select...</option>
            {
              options.map(option => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))
            }
          </select>
        </div>
      </div>
    );
  }

  /* eslint-disable react/no-danger */
  render() {
    const { collectionItem, isDefaultGiftCard } = this.props;
    const { currentAmountKey, showPriceDetails, registry } = this.state;

    if (collectionItem === null) return null;

    // const productImage = collectionItem.images.length > 0 && collectionItem.images[0].medium;
    const isGroupGift = !isDefaultGiftCard && collectionItem.contributions.group_gift;
    const stillNeeds = isDefaultGiftCard ? null : collectionItem.contributions.still_needs;
    const isFulfilled = !isDefaultGiftCard && collectionItem.contributions.show_as_fulfilled;
    const isReserved = !isDefaultGiftCard && collectionItem.contributions.reserved;
    const isMostWanted = collectionItem.most_wanted;
    // const canPurchase = collectionItem.actions && collectionItem.actions.purchase;
    const showContributions = !isDefaultGiftCard && !collectionItem.contributions.hide_contributions; // !(!canPurchase || collectionItem.contributions.hide_contributions || isFulfilled || isReserved);

    const isExternalPurchase = collectionItem.type === 'EXTERNAL' && !collectionItem.cash_fund;
    const isExternalGift = collectionItem.type === 'EXTERNAL' && collectionItem.cash_fund;

    const isFreeShip = collectionItem.free_shipping;
    const isCashFund = collectionItem.type === 'CASH';
    /*
    collectionItem.shippable &&
    !collectionItem.delivery_surcharge &&
    !(isGroupGift && collectionItem.contributions.completed_units > 0);
    */

    const showCsMessage = isDefaultGiftCard || (!isFulfilled && collectionItem.actions.add_to_cart);

    // Group Gift
    let goalPrice;
    let subtotalPrice;
    let additionalFees;
    let hasPriceDetails;
    let showEstimatedArrival;
    let canPurchaseEntireGift;
    if (isGroupGift) {
      goalPrice = !isDefaultGiftCard && collectionItem.contributions.goal_units / 100;
      subtotalPrice = collectionItem.price * collectionItem.requested_quantity;
      additionalFees = Math.max(0, goalPrice - subtotalPrice);
      hasPriceDetails = !!(additionalFees || (collectionItem.requested_quantity > 1));
      showEstimatedArrival = !isDefaultGiftCard && collectionItem.arrival_date_range && !collectionItem.contributions.num_contributors;
      canPurchaseEntireGift = !isDefaultGiftCard && isGroupGift && collectionItem.type === 'ZOLA' && collectionItem.contributions.num_contributors === 0;
    } else {
      goalPrice = 0;
      subtotalPrice = 0;
      additionalFees = 0;
      hasPriceDetails = false;
      showEstimatedArrival = collectionItem.arrival_date_range;
      canPurchaseEntireGift = false;
    }
    let defaultGiftCardImages;
    if (isDefaultGiftCard) {
      const images = (collectionItem.product_look_views && collectionItem.product_look_views[0].image_links);
      defaultGiftCardImages = [{
        thumb: images.A168,
        small: images.A_480,
        medium: images.A_640,
        large: images.A_750,
        full: images.A_1600
      }];
    }

    // Promotional Badge
    let imageHostname = null;
    switch (window.zola.env) {
      case 'production':
        imageHostname = '//images.zola.com';
        break;
      case 'staging':
        imageHostname = '//stage-images.zola.com';
        break;
      default:
        imageHostname = 'localhost:9100/v2/image';
    }
    const badgeImageUrl = (window.zola.feature.productBadging && collectionItem.badge_uuid)
      ? `${imageHostname}/${collectionItem.badge_uuid}`
      : null;

    return (
      <div className="zola-registry-styles">
        <div className="modal-body pdp ridp">
          <div className="row">
            <div className="col-xs-12 col-sm-6 col-xl-7">
              {/* image gallery (visible-all) */}
              <div>
                {isFulfilled && (
                  <div className="shop-badge shop-badge-fulfilled">
                    <div className="shop-badge-purchased-tile">
                      <div className="shop-badge-purchased-tile__content">
                        <i className="zolaicon zolaicon-checkmark" />
                        <div>Purchased</div>
                      </div>
                    </div>
                  </div>
                )}
                {isMostWanted && !isFulfilled && !isReserved && <div className="shop-badge shop-badge-most-wanted" />}
                {isReserved && (
                  <div className="shop-badge shop-badge-on-hold" />
                )}
                {(window.zola.feature.productBadging && collectionItem.badge_uuid && !isGroupGift && !isFulfilled && !isMostWanted)
                  ? <ProductImageGallery images={collectionItem.images} hideZoom={isExternalPurchase || isExternalGift || isCashFund} badgeImageUrl={badgeImageUrl} />
                  : <ProductImageGallery images={collectionItem.images} hideZoom={isExternalPurchase || isExternalGift || isCashFund} />
                }
                {
                  isDefaultGiftCard && <ProductImageGallery images={defaultGiftCardImages} hideZoom />
                }
                {isExternalPurchase && (
                  <small className="text-muted">{registry.registrantFirstName} &amp; {registry.partnerFirstName} added this gift from {collectionItem.store_name}</small>
                )}
              </div>

              {/* product details (visible-mobile) */}
              <div className="shop-entity-details shop-entity-details-big text-center visible-xs-block">
                <h6>
                  {collectionItem.brand && (
                    <a className="brand-name" href={`/shop/brand/${collectionItem.brand.key}`}>{collectionItem.brand.name} &rsaquo;</a>
                  )}
                </h6>
                <h2 className="entity-name">{collectionItem.name}</h2>
                {showContributions && (
                  <div className="price-container">
                    {isGroupGift ? (
                      <h3 className="price">{CompactCurrencyFilter(goalPrice)}</h3>
                    ) : (
                      <h3 className="price"><ProductPrice sku={collectionItem} /></h3>
                    )}
                    {!hasPriceDetails && isFreeShip && !isFulfilled && (
                      <div className="free-ship">Free Standard Shipping</div>
                    )}
                    {hasPriceDetails && (collectionItem.requested_quantity > 1) && (
                      <p> for {collectionItem.requested_quantity} requested</p>
                    )}
                    {hasPriceDetails && (
                      <h5>
                        <a role="button" onClick={this.togglePriceDetails}>
                          {showPriceDetails ? 'Hide' : 'View'} Details
                        </a>
                      </h5>
                    )}
                  </div>
                )}
                {collectionItem.delivery_surcharge > 0 && (
                  <div className="price-detail">Delivery Surcharge: {CompactCurrencyFilter(collectionItem.delivery_surcharge)}</div>
                )}
                {showEstimatedArrival && (
                  <div className="price-detail">We Price Match! <a target="_blank" rel="noreferrer noopener" href="/wedding-registry/benefits/price-match">Learn More</a></div>
                )}
              </div>

              {/* personal note (hidden-mobile) */}
              {collectionItem.personal_note && registry && (
                <div className="personal-note product-editorial-copy hidden-xs">
                  <div className="couple-image-wrapper">
                    <img
                      className="couple-img-circle img-responsive"
                      src={registry.coupleImage}
                      alt={`${registry.registrantFirstName} &amp; ${registry.partnerFirstName} Wedding Registry`}
                    />
                  </div>
                  <div className="personal-note-wrapper">
                    <div className="editorial-copy-text">&ldquo;{collectionItem.personal_note}&rdquo;</div>
                    <h5 className="editorial-copy-header">
                      {registry.registrantFirstName} &amp; {registry.partnerFirstName}
                    </h5>
                  </div>
                </div>
              )}
            </div>

            {/* product details */}
            <div className="col-xs-12 col-sm-6 col-xl-5 col-xl-double-gutter pdp-right">
              {!isExternalPurchase ? (
                <div>
                  {/* product details (hidden-mobile) */}
                  <div className="shop-entity-details shop-entity-details-big hidden-xs">
                    <h6>
                      {collectionItem.brand && (
                        <a className="brand-name" href={`/shop/brand/${collectionItem.brand.key}`} target="_blank" rel="noreferrer noopener">{collectionItem.brand.name} &rsaquo;</a>
                      )}
                    </h6>
                    <h2 className="entity-name">{collectionItem.name}</h2>
                    {showContributions && (
                      <div className="price-container">
                        {isGroupGift ? (
                          <h3 className="price">{CompactCurrencyFilter(goalPrice)}</h3>
                        ) : (
                          <h3 className="price"><ProductPrice sku={collectionItem} /></h3>
                        )}
                        {isFreeShip && !isFulfilled && !isGroupGift && (
                          <div className="free-ship">Free Standard Shipping</div>
                        )}
                        {hasPriceDetails && (collectionItem.requested_quantity > 1) && (
                          <p> for {collectionItem.requested_quantity} requested</p>
                        )}
                        {hasPriceDetails && (
                          <h5 className="pull-right">
                            <a role="button" onClick={this.togglePriceDetails}>
                              {showPriceDetails ? 'Hide' : 'View'} Details
                            </a>
                          </h5>
                        )}
                      </div>
                    )}
                    {collectionItem.delivery_surcharge > 0 && (
                      <div className="price-detail">Delivery Surcharge: {CompactCurrencyFilter(collectionItem.delivery_surcharge)}</div>
                    )}
                    {showEstimatedArrival && (
                      <div className="price-detail">We Price Match! <a target="_blank" rel="noreferrer noopener" href="/wedding-registry/benefits/price-match">Learn More</a></div>
                    )}
                  </div>

                  {/* price details for group gifts */}
                  {showPriceDetails && (
                    <div className="price-details container-fluid">
                      <div className="row price-details-header">
                        <div className="col-xs-6">Unit Price</div>
                        <div className="col-xs-3">Quantity</div>
                        <div className="col-xs-3 text-right">Subtotal</div>
                      </div>
                      <div className="row price-item">
                        <div className="col-xs-6"><ProductPrice sku={collectionItem} /></div>
                        <div className="col-xs-3">{collectionItem.requested_quantity}</div>
                        <div className="col-xs-3 text-right">{CompactCurrencyFilter(subtotalPrice)}</div>
                      </div>
                      <div className="row totals">
                        {additionalFees > 0 && (
                          <div className="additional-fees">
                            <div className="col-xs-8 text-right">Est Shipping &amp; Tax</div>
                            <div className="col-xs-4 text-right">{CompactCurrencyFilter(additionalFees)}</div>
                          </div>
                        )}
                        <div className="total">
                          <div className="col-xs-8 text-right">Group Gift Total</div>
                          <div className="col-xs-4 text-right">{CompactCurrencyFilter(goalPrice)}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    {/* Zola Checkout Purchase */}
                    {isFulfilled ? (
                      <div className="fulfilled-message">
                        <h5 className="registry-item-status fulfilled">
                          <span className="zolaicon zolaicon-checkmark" />
                          Purchased
                        </h5>
                      </div>
                    ) : (
                      <div>
                        {/* Group Gift */}
                        {isGroupGift && showContributions && (
                          <div className="group-gift">
                            <div className="form-label">Group Gifting <small className="text-normal"><a role="button" data-tooltip={GROUP_GIFT_COPY}>Learn More</a></small>
                            </div>
                            <div className="progress-wrapper">
                              <div className="progress">
                                <div className="progress-bar progress-bar-success" role="progressbar" aria-valuenow={collectionItem.contributions.percent_complete} aria-valuemin="0" aria-valuemax="100" style={{ width: `${collectionItem.contributions.percent_complete < 100 ? collectionItem.contributions.percent_complete : 100}%` }} aria-valuetext={`${collectionItem.contributions.percent_complete < 100 ? collectionItem.contributions.percent_complete : 100}%`} aria-labelledby="progressbar" />
                              </div>
                              <h5 className="percent-complete">{collectionItem.contributions.percent_complete}%</h5>
                            </div>
                            {collectionItem.contributions.num_contributors > 0 && (
                              <div className="value-label">
                                {`${collectionItem.contributions.num_contributors} ${collectionItem.contributions.num_contributors === 1 ? 'Contribution' : 'Contributions'}`}
                              </div>
                            )}
                          </div>
                        )}
                        {/* Group Gift, not fulfilled */}
                        {isGroupGift && !isFulfilled && (
                          <div className="contribute">
                            {window.zola.env !== 'production' ? (
                              <div className="product-inputs form-inline">
                                <div className="product-input form-group">
                                  <label htmlFor="group-gift-contribution">Contribute:</label>
                                  <div className="input-wrapper">
                                    <div className="input-group">
                                      <span className="input-group-addon">$</span>
                                      <input id="group-gift-contribution" type="number" min=".01" step="0.01" pattern="^[0-9]+(\.[0-9]{1,2})?$" className="form-control input-narrow" required value={this.state.contribution} onChange={this.updateContribution} />
                                    </div>
                                    {showContributions && (
                                      <span className="text-secondary contributions">Still Needs: {stillNeeds}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="product-inputs form-inline">
                                <div className="product-input form-group">
                                  <label htmlFor="group-gift-contribution">Contribute:</label>
                                  <div className="input-wrapper">
                                    <div className="input-group">
                                      <span className="input-group-addon">$</span>
                                      <input id="group-gift-contribution" type="number" min=".01" step="0.01" pattern="^[0-9]+(\.[0-9]{1,2})?$" className="form-control input-narrow" required value={this.state.contribution} onChange={this.updateContribution} />
                                    </div>
                                    {showContributions && (
                                      <span className="text-secondary contributions">Still Needs: {stillNeeds}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {/* Quantity Purchase */}
                        {!isGroupGift && (
                          <div className="product-inputs form-inline">
                            {/* sku attributes */}
                            {!isDefaultGiftCard && collectionItem.sku_attributes.map(skuAttribute => (
                              <div key={skuAttribute.label} className="product-input form-group">
                                <span className="form-label">{skuAttribute.label}:</span> <span className="text-secondary">{skuAttribute.value}</span>
                              </div>
                            ))}
                            {/* price options for default card price select input */}
                            {isDefaultGiftCard && this.renderDefaultGiftCardOptions(collectionItem)}
                            {/* quantity input */}
                            <div className="product-input form-group">
                              <label htmlFor="collection-item-quantity">Quantity:</label>
                              <div className="input-wrapper">
                                <input id="collection-item-quantity" type="number" className="form-control input-narrow" name="quantity" min="1" max={stillNeeds} value={this.state.quantity} onChange={this.updateQuantity} />
                                {!isDefaultGiftCard && <span className="text-secondary contributions">Still Needs: {stillNeeds}</span>}
                              </div>
                            </div>
                          </div>
                        )}
                        {/* product actions */}
                        <div className="wrapping-button-container product-actions">
                          {/* add to cart button */}
                          <div className="add-to-cart btn-tooltip-wrapper">
                            <button
                              type="button"
                              className="btn btn-primary btn-block"
                              disabled={(isDefaultGiftCard && !currentAmountKey) || (!isDefaultGiftCard && !collectionItem.actions.add_to_cart)}
                              onClick={this.addToCart}
                            >
                              <span>
                                {isGroupGift ? 'Contribute To Gift' : 'Buy Now'}
                              </span>
                            </button>
                          </div>
                          {/* buy entire group gift button (Only for Group Gifts) */}
                          {canPurchaseEntireGift && (
                            <button
                              type="button"
                              className="btn btn-secondary btn-block"
                              disabled={!collectionItem.actions.add_to_cart}
                              onClick={this.buyGroupGift}
                            >
                              Buy Entire Gift
                            </button>
                          )}
                        </div>
                        {/* cs message */}
                        {showCsMessage && (
                          <div className="cs-message">
                            To order by phone, call <span className="semibold">408-657-ZOLA </span>(9652)
                          </div>
                        )}
                        {/* stock message */}
                        {collectionItem.stock_message && (
                          <div className="stock-message">
                            <em className="text-muted">{collectionItem.stock_message}</em>
                          </div>
                        )}
                        {/* External Gift Disclaimer */}
                        {isExternalGift && (
                          <div className="external-copy">
                            This product has been added by the couple from another website and cannot be shipped to you, the couple will redeem it at their convenience.
                          </div>
                        )}
                        {/* estimated arrival */}
                        {showEstimatedArrival && (
                          <div className="estimated-arrival">
                            <div className="form-label"><strong>Estimated Arrival</strong></div>
                            <div className="standard-shipping">
                              {!hasPriceDetails && isFreeShip && !isFulfilled && (
                                <span>Free standard </span>
                              )}
                              {hasPriceDetails && !isFreeShip && isFulfilled && (
                                <span>Standard </span>
                              )}
                              Shipping: <span className="semibold"><DateRange dateRange={collectionItem.arrival_date_range} /></span>
                            </div>
                            {collectionItem.expeditable_date_range && (
                              <div>
                                Two-day shipping ($19.95): <span className="semibold"><DateRange dateRange={collectionItem.expeditable_date_range} /></span> if you order by 1PM EST
                              </div>
                            )}
                            <div>{collectionItem.custom_ship_message}</div>
                          </div>
                        )}
                      </div>
                    )}
                    {/* info tabs */}
                    {(isDefaultGiftCard || collectionItem.type === 'ZOLA') && (
                      <ProductInfoTabs collectionItem={collectionItem} />
                    )}
                    {/* partner retailer notice */}
                    {collectionItem.partner_retailer && (
                      <div className="partner-retailer-notice">{collectionItem.partner_retailer.supportNotice}</div>
                    )}
                  </div>
                </div>
              ) : (
                <ExternalPurchaseFlow collectionItem={collectionItem} registry={registry} />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

RegistryItemDp.propTypes = {
  collectionItem: PropTypes.shape({}),
  isDefaultGiftCard: PropTypes.bool,
  registryId: PropTypes.string.isRequired,
  onAddToCart: PropTypes.func.isRequired,
  onCart: PropTypes.func.isRequired,
  getRegistryByCollectionItemId: PropTypes.func.isRequired,
};

const mapStateToProps = state => (
  {
    registryId: state.registry.registry_object_id || state.publicWebsite.zolaRegistry.registry_object_id,
  }
);

const mapDispatchToProps = dispatch => ({
  onAddToCart: addItemRequest => dispatch(addItem(addItemRequest)),
  onCart: () => dispatch(showModal('CART_MODAL')),
  getRegistryByCollectionItemId: collectionItemId => dispatch(fetchRegistryByCollectionItemId(collectionItemId))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(RegistryItemDp);



// WEBPACK FOOTER //
// ./src/components/registryitem/RegistryItem.jsx